import type {FC} from 'react';
import reactStringReplace from 'react-string-replace';

import {Observer} from 'mobx-react';

import {useI18n} from '@yourcoach/i18n';

import type {GetComponentProps} from '../../../../utils/utility-types';
import type {CardsListItem} from '../CardsListItem';
import type {Props} from './types';

import * as S from './styles';

export const MakeRefundContainer: FC<Props> = ({
  onAddCardButtonClick,
  onAmountFocus,
  store,
}) => {
  const {t} = useI18n();
  const onCardClick: GetComponentProps<typeof CardsListItem>['onClick'] = (
    _e,
    card,
  ) => {
    store.setSelectedCard(card);
  };

  const renderClientName = () =>
    store.payment?.transferToMe ? (
      <S.Row title={t('shared:client_name')} value={store.payment?.userName} />
    ) : null;

  const renderTotalPaid = () => (
    <S.Row
      title={t('shared:total_paid')}
      value={store.payment!.totalAmountString}
      variant="highlighted"
    />
  );

  const renderTransferAmount = () => (
    <>
      <S.Row
        title={t('shared:stripe_commission')}
        value={store.payment!.taxString}
        variant="highlighted"
      />
      <S.Row
        title={t('shared:total_received')}
        value={store.payment!.transferToMeString}
        variant="highlighted"
      />
    </>
  );

  const renderRefundAmount = () =>
    store.payment?.refundedAmount ? (
      <S.Row
        title={t('shared:refunded')}
        value={store.payment?.refundedAmountString}
        variant="highlighted"
      />
    ) : null;

  const renderAmount = () =>
    store.payment?.transferToMe ? renderTransferAmount() : renderRefundAmount();

  const renderDate = () => (
    <S.Row title={t('shared:payment_date')} value={store.payment!.dateString} />
  );

  const renderCourseInfo = () =>
    store.payment?.course ? (
      <>
        <S.Row
          title={t('shared:program_label')}
          value={store.payment.programTitle}
        />
        <S.Row title={t('shared:dates')} value={store.payment.courseLabel} />
      </>
    ) : null;

  const renderGeneralInfo = () => (
    <Observer>
      {() => (
        <>
          {renderClientName()}
          {renderTotalPaid()}
          {renderAmount()}
          {renderDate()}
          {renderCourseInfo()}
        </>
      )}
    </Observer>
  );

  const renderAmountInput = () => (
    <S.AmountInputContainer>
      <S.Label>{t('shared:amount')}</S.Label>
      <Observer>
        {() => (
          <S.AmountInput
            error={store.fields.amount.error || ''}
            inputMode="numeric"
            nativeProps={{
              returnKeyType: 'done',
            }}
            onChange={store.onChangeAmount}
            onFocus={onAmountFocus}
            placeholder={store.amountInputPlaceholder}
            ref={store.fields.amount.ref}
            value={store.amountInputValue}
          />
        )}
      </Observer>
    </S.AmountInputContainer>
  );

  const renderTaxInfo = () => (
    <Observer>
      {() =>
        store.refundBy === 'card' ? (
          <S.TaxContainer>
            <S.TaxIcon />
            <S.Tax>
              {reactStringReplace(
                t('shared:total_cost_and_tax', {
                  amount: store.amountWithTaxString,
                  tax: store.taxString,
                }),
                /<b>(.+?)<\/b>/gi,
                (match, i) => (
                  <S.TaxHighlight key={i}>{match}</S.TaxHighlight>
                ),
              )}
            </S.Tax>
          </S.TaxContainer>
        ) : null
      }
    </Observer>
  );

  const renderGreaterAmountMessage = () => (
    <Observer>
      {() => (
        <S.Description>
          {store.refundBy === 'card'
            ? t('shared:refund_description_2')
            : t('shared:refund_description_1')}
        </S.Description>
      )}
    </Observer>
  );

  const renderNoteInput = () => (
    <Observer>
      {() => (
        <S.NoteInputContainer>
          <S.NoteLabelContainer>
            <S.Label>{t('shared:note')}</S.Label>
            <Observer>
              {() =>
                !store.isNoteFieldVisible ? (
                  <S.AddNoteButton
                    onClick={() => store.setNoteFieldVisibility(true)}
                  >
                    <S.AddNoteIcon />
                    <S.AddNoteButtonText>
                      {t('shared:add_note')}
                    </S.AddNoteButtonText>
                  </S.AddNoteButton>
                ) : null
              }
            </Observer>
          </S.NoteLabelContainer>
          <Observer>
            {() =>
              store.isNoteFieldVisible ? (
                <S.NoteInput
                  error={store.fields.note.error || ''}
                  onBlur={store.fields.note.onBlur}
                  onChange={store.fields.note.onChange}
                  onFocus={store.fields.note.onFocus}
                  placeholder={t('shared:add_note')}
                  ref={store.fields.note.ref}
                  value={store.fields.note.value}
                />
              ) : null
            }
          </Observer>
        </S.NoteInputContainer>
      )}
    </Observer>
  );

  const renderCardError = () => (
    <Observer>
      {() =>
        store.refundBy === 'card' && store.cardError ? (
          <S.CardError>{store.cardError}</S.CardError>
        ) : null
      }
    </Observer>
  );

  const renderStripeRefundMethod = () => (
    <Observer>
      {() => {
        const isActive = store.refundBy === 'stripe';

        return (
          <S.RefundMethodButton
            $isActive={isActive}
            disabled={store.payment.availableAmountToRefund.byStripe === 0}
            onClick={() => store.setRefundBy('stripe')}
          >
            <S.StripeIconContainer $isActive={isActive}>
              <S.StripeIcon $isActive={isActive} />
            </S.StripeIconContainer>
            <S.RefundMethodButtonText $isActive={isActive}>
              {t('shared:stripe_account')}
            </S.RefundMethodButtonText>
          </S.RefundMethodButton>
        );
      }}
    </Observer>
  );

  const renderCardRefundMethod = () => (
    <Observer>
      {() => {
        const isActive = store.refundBy === 'card';

        return (
          <S.RefundMethodButton
            $isActive={isActive}
            onClick={() => store.setRefundBy('card')}
          >
            <S.CardIcon $isActive={isActive} />
            <S.RefundMethodButtonText $isActive={isActive}>
              {t('shared:card')}
            </S.RefundMethodButtonText>
          </S.RefundMethodButton>
        );
      }}
    </Observer>
  );

  const renderStripeRefundDescription = () => (
    <Observer>
      {() =>
        store.refundBy === 'stripe' ? (
          <S.StripeDescription>{t('shared:stripe_refund')}</S.StripeDescription>
        ) : null
      }
    </Observer>
  );

  const renderCardsList = () => (
    <Observer>
      {() =>
        store.refundBy === 'card' ? (
          <>
            <S.AddCardButton onClick={onAddCardButtonClick}>
              <S.AddCardIcon />
              <S.AddCardButtonText>
                {t('shared:add_card_label')}
              </S.AddCardButtonText>
            </S.AddCardButton>
            {store.cards.map(card => (
              <S.CardsListItem
                card={card}
                isSelected={store.selectedCard?._id === card._id}
                key={card._id}
                onClick={onCardClick}
              />
            ))}
          </>
        ) : null
      }
    </Observer>
  );

  return (
    <S.Container>
      {renderGeneralInfo()}
      {renderAmountInput()}
      {renderTaxInfo()}
      {renderGreaterAmountMessage()}
      {renderNoteInput()}
      <S.Label>{t('shared:refund_from')}</S.Label>
      <S.RefundMethodsContainer>
        {renderCardError()}
        {renderStripeRefundMethod()}
        <S.RefundMethodsGap />
        {renderCardRefundMethod()}
      </S.RefundMethodsContainer>
      {renderStripeRefundDescription()}
      {renderCardsList()}
    </S.Container>
  );
};
